import React, { useCallback, useMemo } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { Breadcrumb } from '../../types/Folder';
import useStyles from './FolderBreadcrumbs.styles';
import BreadcrumbItem from './BreadcrumbItem';
import { useBreadcrumbMenu } from './useBreadcrumbMenu';

interface FolderBreadcrumbsProps {
  breadcrumbs: Breadcrumb[];
  onNavigate: (id: string) => void;
}

const VISIBLE_EDGE = 1;

const Separator: React.FC = () => {
  const classes = useStyles();
  return <span className={classes.separator}>/</span>;
};

const FolderBreadcrumbs: React.FC<FolderBreadcrumbsProps> = ({
  breadcrumbs,
  onNavigate,
}) => {
  const classes = useStyles();
  const { menuAnchor, openMenu, closeMenu } = useBreadcrumbMenu();

  const shouldCollapse = useMemo(
    () => breadcrumbs.length > VISIBLE_EDGE * 2 + 1,
    [breadcrumbs.length]
  );

  const { leadingCrumbs, hiddenCrumbs, trailingCrumbs } = useMemo(() => {
    if (!shouldCollapse)
      return { leadingCrumbs: [], hiddenCrumbs: [], trailingCrumbs: [] };
    return {
      leadingCrumbs: breadcrumbs.slice(0, VISIBLE_EDGE),
      hiddenCrumbs: breadcrumbs.slice(
        VISIBLE_EDGE,
        breadcrumbs.length - VISIBLE_EDGE
      ),
      trailingCrumbs: breadcrumbs.slice(breadcrumbs.length - VISIBLE_EDGE),
    };
  }, [breadcrumbs, shouldCollapse]);

  const handleHiddenNavigate = useCallback(
    (id: string) => {
      closeMenu();
      onNavigate(id);
    },
    [closeMenu, onNavigate]
  );

  if (!breadcrumbs.length) return null;

  if (!shouldCollapse) {
    return (
      <Box className={classes.root}>
        {breadcrumbs.map((crumb, i) => (
          <React.Fragment key={crumb.id}>
            <BreadcrumbItem
              crumb={crumb}
              isCurrent={i === breadcrumbs.length - 1}
              onNavigate={onNavigate}
            />
            {i < breadcrumbs.length - 1 && <Separator />}
          </React.Fragment>
        ))}
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {leadingCrumbs.map((crumb) => (
        <React.Fragment key={crumb.id}>
          <BreadcrumbItem
            crumb={crumb}
            isCurrent={false}
            onNavigate={onNavigate}
          />
          <Separator />
        </React.Fragment>
      ))}

      <Tooltip
        title={`${hiddenCrumbs.length} more folders`}
        placement="top"
        PopperProps={{ disablePortal: true }}
      >
        <IconButton
          className={classes.moreButton}
          size="small"
          onClick={openMenu}
          aria-label="Show more breadcrumbs"
        >
          <MoreHoriz />
        </IconButton>
      </Tooltip>
      <Separator />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
      >
        {hiddenCrumbs.map((crumb) => (
          <MenuItem
            key={crumb.id}
            onClick={() => handleHiddenNavigate(crumb.id)}
          >
            {crumb.name}
          </MenuItem>
        ))}
      </Menu>

      {trailingCrumbs.map((crumb, i) => (
        <BreadcrumbItem
          key={crumb.id}
          crumb={crumb}
          isCurrent={i === trailingCrumbs.length - 1}
          onNavigate={onNavigate}
        />
      ))}
    </Box>
  );
};

export default React.memo(FolderBreadcrumbs);
